/**
 * formatters.js - Utility functions for formatting and transforming data
 * Contains functions for formatting text, numbers, and transforming data structures
 */

/**
 * Shortens agency names for display purposes
 * @param {string} name - The full agency name
 * @returns {string} The shortened agency name
 */
export function shortenAgencyName(name) {
  if (!name) return 'Unknown';
  
  // Expanded abbreviations with better formatting
  const abbreviations = {
    // Departments
    'Department of Infrastructure': 'Infrastructure',
    'Department of Infrastructure, Transport': 'Infrastructure & Transport',
    'Department of Health': 'Health',
    'Department of Health and Aged Care': 'Health & Aged Care',
    'Department of Education': 'Education',
    'Department of Education, Skills': 'Education & Skills',
    'Department of Social Services': 'Social Services',
    'Department of Climate Change': 'Climate Change',
    'Department of Climate Change, Energy': 'Climate & Energy',
    'Department of Industry, Science': 'Industry & Science',
    'Department of Industry, Science and Resources': 'Industry & Resources',
    'Department of the Prime Minister': 'Prime Minister & Cabinet',
    'Department of Agriculture': 'Agriculture',
    'Department of Agriculture, Fisheries': 'Agriculture & Fisheries',
    'Department of Agriculture, Water': 'Agriculture & Water',
    'Department of Employment': 'Employment',
    'Department of Employment and Workplace': 'Employment & Workplace',
    'Department of Home Affairs': 'Home Affairs',
    'Department of Foreign Affairs': 'Foreign Affairs',
    'Department of Defence': 'Defence',
    'Department of Veterans\' Affairs': 'Veterans\' Affairs',
    'Department of Finance': 'Finance',
    'Department of the Treasury': 'Treasury',
    'Department of the Environment': 'Environment',
    'Department of Communications': 'Communications',
    'Attorney-General\'s Department': 'Attorney-General',
    
    // Agencies and councils
    'Australian Research Council': 'Research Council',
    'National Indigenous Australians Agency': 'Indigenous Australians Agency',
    'National Health and Medical Research Council': 'Health & Medical Research',
    'National Emergency Management Agency': 'Emergency Management',
    'Organ and Tissue Authority': 'Organ & Tissue Authority',
    'National Disability Insurance Agency': 'NDIS Agency',
    'Australian Trade and Investment Commission': 'Trade & Investment',
    'Australian Federal Police': 'Federal Police',
    'Australian Taxation Office': 'Tax Office',
    'Australian Bureau of Statistics': 'Bureau of Statistics',
    'Australian Electoral Commission': 'Electoral Commission',
    'Australian Communications and Media Authority': 'Communications Authority',
    'Murray-Darling Basin Authority': 'Murray-Darling Authority'
  };
  
  // Try direct matching first
  for (const [full, abbr] of Object.entries(abbreviations)) {
    if (name.includes(full)) {
      return abbr;
    }
  }
  
  // Handle Department of X format consistently
  if (name.includes('Department of')) {
    // Extract the part after "Department of" and before any comma
    let mainPart = name.split('Department of ')[1];
    if (mainPart.includes(',')) {
      mainPart = mainPart.split(',')[0];
    }
    // Capitalize first letter of each word
    mainPart = mainPart.replace(/\b\w/g, c => c.toUpperCase());
    return mainPart;
  }
  
  // Handle agencies, authorities, commissions
  if (name.includes('Agency') || name.includes('Authority') || name.includes('Commission')) {
    // If there's a comma, take the first part
    if (name.includes(',')) {
      return name.split(',')[0];
    }
    
    // Try to extract the main name without the type
    const types = ['Agency', 'Authority', 'Commission'];
    for (const type of types) {
      if (name.includes(` ${type}`)) {
        // Return the part before " Agency/Authority/Commission"
        return name.split(` ${type}`)[0];
      }
    }
  }
  
  // If name is still long but doesn't match any patterns, trim it with a smarter approach
  if (name.length > 25) {
    // Try to find a natural breaking point (space) before 22 chars
    const lastSpace = name.substring(0, 22).lastIndexOf(' ');
    if (lastSpace > 10) { // Only use this approach if we can keep a substantial part
      return name.substring(0, lastSpace) + '...';
    }
    // Fallback to simple truncation
    return name.substring(0, 22) + '...';
  }
  
  return name;
}

/**
 * Formats a date string to a more readable format
 * @param {string} dateString - The date string to format
 * @returns {string} The formatted date string
 */
export function formatDate(dateString) {
  if (!dateString) return 'N/A';
  
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', options);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
}

/**
 * Prepares yearly chart data from raw electorate data
 * @param {Object} byYear - The yearly data object
 * @returns {Object} Formatted chart data for LineChart component
 */
export function prepareYearlyChartData(byYear) {
  if (!byYear || Object.keys(byYear).length === 0) {
    return {
      labels: [],
      datasets: [{
        label: 'Funding',
        data: [],
        borderColor: 'rgba(56, 189, 248, 1)',
        backgroundColor: 'rgba(56, 189, 248, 0.2)',
        borderWidth: 2
      }]
    };
  }
  
  const years = Object.keys(byYear).sort();
  const values = years.map(year => byYear[year]);
  
  return {
    labels: years,
    datasets: [
      {
        label: 'Funding',
        data: values,
        borderColor: 'rgba(56, 189, 248, 1)',
        backgroundColor: 'rgba(56, 189, 248, 0.2)',
        borderWidth: 2,
        tension: 0.1
      }
    ]
  };
}

/**
 * Prepares agency chart data from raw electorate data
 * @param {Object} byAgency - The agency data object
 * @param {number} limit - Maximum number of agencies to include
 * @returns {Array} Formatted chart data for PieChart component
 */
export function prepareAgencyChartData(byAgency, limit = 6) {
  if (!byAgency || Object.keys(byAgency).length === 0) {
    return [];
  }
  
  return Object.entries(byAgency)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([name, value]) => ({
      name,
      value
    }));
} 